import { Member } from "../../../types/employee.types";

import { GroupOwner } from "../../../types/groupOwner.types";
import { getCoveredDependents, getKemperCancerCoveredDependents } from "./coveredDependents";
import { dateTimeZoneConverter, getCurrentDate, getFirstOfNextMonth } from "../date";
import {
	Enrollment,
	EnrollmentStandardDetails,
	EnrollmentCommonDetails,
	VEBEnrollmentCommonDetails,
	VEBEnrollmentStandardDetails,
	CoverageCode
} from "../../../types/enrollment.types";
import { VEBPlan } from "../../../types/plan.types";
import MEMBER_WRITING_NUMBER from "../../../constants/writingNumber";
import { Paycheck } from "../../../types/paycheck.types";
import { VEBPlanCodes2024, VEBPlanCodes2025 } from "../../../constants/VEBPlanCodes/VEBPlanCodes2024";
import { VEBYearlyOpenAndCurrentEffectiveDate } from "../../../constants/VEBYearlyOpenAndCurrentDate";
import { VEBPlanSchema } from "../../../types/VEBPlanSchema";

export const generateActivateEnrollmentPayload = (
	plan: any,
	groupOwner: GroupOwner | null,
	member: Member,
	coverage: string,
	premiumAmount: number,
	eligibleDependents: Member[]
): Enrollment => {
	const enrollmentCommonDetails: EnrollmentCommonDetails = {
		agent_id: groupOwner && groupOwner._id ? groupOwner._id : null,
		location_number: member.location_number,
		location_name: member.location?.location_name ?? "",
		group_number: member.group_number,
		group_name: member.group?.name ?? "",
		plan_object_id: plan._id,
		plan_code: plan.plan_code,
		enrollment_status: "APPROVED",
		insured_object_id: member._id,
		insured_SSN: member.SSN,
		unenrolled_reason: null,
		waive_reason: null,
		termination_reason: null,
		enrollment_date: getCurrentDate(),
		effective_date: getFirstOfNextMonth(),
		termination_date: null,
		open_enrollment_id: "0xqwe123123"
	};
	// console.log("enrollmentCommonDetails", getFirstOfNextMonth());
	const enrollmentStandardDetails: EnrollmentStandardDetails[] = [
		{
			member_object_id: member._id,
			member_SSN: member.SSN,
			premium_amount: premiumAmount,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			coverage_code: coverage
		}
	];
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const coveredDependents = getCoveredDependents(coverage, eligibleDependents);
	const member_SSNs = [...coveredDependents.dep_SSNs, member.SSN];
	const enrollment: Enrollment = {
		standard_details: enrollmentStandardDetails.concat(coveredDependents.enrollmentStandardDetails),
		common_details: enrollmentCommonDetails,
		dep_SSNs: member_SSNs
	};

	return enrollment;
};

export const generateActivateVEBEnrollmentPayload = (
	plan: VEBPlanSchema,
	groupOwner: GroupOwner | null,
	member: Member,
	planFor: Member,
	benefitAmount: number,
	coverageCode: CoverageCode | string,
	premiumAmount: number,
	eligibleDependents: Member[],
	planName: string,
	totalPremium: number,
	basePlanName: string,
	subPlan: string | null,
	openEnrollmentId: string,
	paycheck: Paycheck,
	writingNumber = MEMBER_WRITING_NUMBER,
	VEB_primary_beneficiary?: any[],
	VEB_contingent_beneficiary?: any[],
	accidental_death_chronic_illness?: any,
	openEnrollmentType?: string
): {
	VEBEnrollmentCommonDetails: VEBEnrollmentCommonDetails;
	VEBEnrollmentStandardDetails: VEBEnrollmentStandardDetails[];
} => {
	const isYearlyOpenEnrollment = openEnrollmentType === "YEARLY";

	console.log("Enrollment Debug", {
		planCode: plan.plan_code,
		openEnrollmentType: openEnrollmentType,
		isYearlyOpenEnrollment: isYearlyOpenEnrollment,
		effectiveDate: isYearlyOpenEnrollment
			? VEBYearlyOpenAndCurrentEffectiveDate.veb_yearly_open_effective_date_YYYYMMDD
			: VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_YYYYMMDD
	});

	const VEBEnrollmentCommonDetails: VEBEnrollmentCommonDetails = {
		agent_id: groupOwner && groupOwner._id ? groupOwner._id : null,
		location_number: member.location_number,
		location_name: member.location?.location_name ?? "",
		group_number: member.group_number,
		group_name: member.group?.name ?? "",
		plan_object_id: plan._id,
		plan_code: plan.plan_code,
		enrollment_status: null,
		insured_object_id: member._id,
		insured_SSN: member.SSN,
		unenrolled_reason: null,
		waive_reason: null,
		termination_reason: null,
		enrollment_date: getCurrentDate(),
		// FIXED: Use isYearlyOpenEnrollment instead of VEBPlanCodes2025 check
		effective_date: isYearlyOpenEnrollment
			? VEBYearlyOpenAndCurrentEffectiveDate.veb_yearly_open_effective_date_YYYYMMDD
			: VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_YYYYMMDD,
		// effective_date: "05-01-2026",
		termination_date: null,
		open_enrollment_id: openEnrollmentId,
		base_plan_name: basePlanName,
		plan_name: planName,
		sub_plan: subPlan,
		writing_number: writingNumber,
		total_premium: totalPremium,
		enrollment_paycheck_id: paycheck._id
	};

	const VEBEnrollmentStandardDetails: VEBEnrollmentStandardDetails[] = [
		{
			member_object_id: member._id,
			member_SSN: member.SSN,
			premium_amount: premiumAmount,
			coverage_code: coverageCode,
			benefit_amount: benefitAmount,
			is_tobacco: member.is_tobacco,
			VEB_primary_beneficiary,
			VEB_contingent_beneficiary
		}
	];

	const coveredDependents = getKemperCancerCoveredDependents(coverageCode, benefitAmount, eligibleDependents);

	return {
		VEBEnrollmentCommonDetails,
		VEBEnrollmentStandardDetails: VEBEnrollmentStandardDetails.concat(coveredDependents)
	};
};
export const generateWholeLifeActivateVEBEnrollmentPayload = (
	plan: VEBPlanSchema,
	groupOwner: GroupOwner | null,
	member: Member,
	planFor: Member,
	benefitAmount: number,
	coverageCode: CoverageCode,
	premiumAmount: number,
	planName: string,
	totalPremium: number,
	basePlanName: string,
	subPlan: string | null,
	openEnrollmentId: string,
	paycheck: Paycheck,
	VEB_primary_beneficiary: any[],
	VEB_contingent_beneficiary: any[],
	writingNumber = MEMBER_WRITING_NUMBER,
	accidental_death_chronic_illness?: any,
	waiver_of_premium?: any
): {
	VEBEnrollmentCommonDetails: VEBEnrollmentCommonDetails;
	VEBEnrollmentStandardDetails: VEBEnrollmentStandardDetails;
} => {
	const VEBEnrollmentCommonDetails: VEBEnrollmentCommonDetails = {
		agent_id: groupOwner && groupOwner._id ? groupOwner._id : null,
		location_number: member.location_number,
		location_name: member.location?.location_name ?? "",
		group_number: member.group_number,
		group_name: member.group?.name ?? "",
		plan_object_id: plan._id,
		plan_code: plan.plan_code,
		enrollment_status: null,
		insured_object_id: member._id,
		insured_SSN: member.SSN,
		unenrolled_reason: null,
		waive_reason: null,
		termination_reason: null,
		enrollment_date: getCurrentDate(),
		effective_date: plan.plan_code
			? VEBPlanCodes2025.includes(plan.plan_code as unknown as string)
				? VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_YYYYMMDD
				: VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_YYYYMMDD
			: null, // yyyy-mm-dd
		// effective_date: getFirstOfNextMonth(), // yyyy-mm-dd
		termination_date: null,
		open_enrollment_id: openEnrollmentId,
		base_plan_name: basePlanName,
		plan_name: planName,
		sub_plan: subPlan,
		writing_number: writingNumber,
		total_premium: totalPremium,
		enrollment_paycheck_id: paycheck._id
	};
	const VEBEnrollmentStandardDetails: VEBEnrollmentStandardDetails = {
		member_object_id: member._id,
		member_SSN: planFor.SSN,
		premium_amount: premiumAmount,
		coverage_code: coverageCode,
		benefit_amount: benefitAmount,
		is_tobacco: planFor.is_tobacco,
		VEB_primary_beneficiary,
		VEB_contingent_beneficiary,
		...(accidental_death_chronic_illness ? { accidental_death_chronic_illness } : {}),
		...(waiver_of_premium ? { waiver_of_premium } : {})
	};
	return {
		VEBEnrollmentCommonDetails,
		VEBEnrollmentStandardDetails
	};
};

export const generateWaiveEnrollmentPayload = (
	plan: any,
	groupOwner: GroupOwner | null,
	member: Member,
	waiveReason: string
): Enrollment => {
	const enrollmentCommonDetails: EnrollmentCommonDetails = {
		agent_id: groupOwner && groupOwner._id ? groupOwner._id : null,
		location_number: member.location_number,
		location_name: member.location?.location_name ?? "",
		group_number: member.group_number,
		group_name: member.group?.name ?? "",
		plan_object_id: plan._id,
		plan_code: plan.plan_code,
		enrollment_status: "WAIVED",
		insured_object_id: member._id,
		insured_SSN: member.SSN,
		unenrolled_reason: null,
		waive_reason: waiveReason,
		termination_reason: null,
		enrollment_date: getCurrentDate(),
		effective_date: null,
		termination_date: null,
		open_enrollment_id: "0xqwe123123"
	};
	// console.log("enrollmentCommonDetails", getFirstOfNextMonth());
	const enrollmentStandardDetails: EnrollmentStandardDetails[] = [
		{
			member_object_id: member._id,
			member_SSN: member.SSN,
			premium_amount: 0,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			coverage_code: ""
		}
	];
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const member_SSNs = [member.SSN];
	const enrollment: Enrollment = {
		standard_details: enrollmentStandardDetails,
		common_details: enrollmentCommonDetails,
		dep_SSNs: member_SSNs
	};

	return enrollment;
};

export const generateWaiveVEBEnrollmentPayload = (
	plan: VEBPlanSchema,
	planName: string,
	basePlanName: string,
	subPlan: string | null,
	groupOwner: GroupOwner | null,
	member: Member,
	waiveReason: string,
	openEnrollmentId: string,
	isTobacco: boolean,
	writingNumber = MEMBER_WRITING_NUMBER
): {
	VEBEnrollmentCommonDetails: VEBEnrollmentCommonDetails;
	VEBEnrollmentStandardDetails: VEBEnrollmentStandardDetails[];
} => {
	// console.log("<===plan====>", plan);
	const VEBEnrollmentCommonDetails: VEBEnrollmentCommonDetails = {
		agent_id: groupOwner && groupOwner._id ? groupOwner._id : null,
		location_number: member.location_number,
		location_name: member.location?.location_name ?? "",
		group_number: member.group_number,
		group_name: member.group?.name ?? "",
		plan_object_id: plan._id,
		plan_code: plan.plan_code,
		enrollment_status: "WAIVED",
		insured_object_id: member._id,
		insured_SSN: member.SSN,
		unenrolled_reason: null,
		waive_reason: waiveReason,
		termination_reason: null,
		enrollment_date: getCurrentDate(),
		effective_date: null,
		termination_date: null,
		open_enrollment_id: openEnrollmentId,
		base_plan_name: basePlanName,
		plan_name: planName,
		sub_plan: subPlan,
		writing_number: writingNumber,
		total_premium: 0
	};
	const VEBEnrollmentStandardDetails: VEBEnrollmentStandardDetails[] = [
		{
			member_object_id: member._id,
			member_SSN: member.SSN,
			premium_amount: 0,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			coverage_code: "EE",
			benefit_amount: 0,
			is_tobacco: isTobacco
		}
	];
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	return {
		VEBEnrollmentCommonDetails,
		VEBEnrollmentStandardDetails
	};
};

export const generateCancerKemperActivateEnrollmentPayload = generateActivateEnrollmentPayload;
export const generateCancerKemperWaiveEnrollmentPayload = generateWaiveVEBEnrollmentPayload;
export const generateCriticalIllnessWaiveEnrollmentPayload = generateWaiveVEBEnrollmentPayload;
export const generateHospitalKemperWaiveEnrollmentPayload = generateWaiveVEBEnrollmentPayload;
export const generateBeamVisionWaiveEnrollmentPayload = generateWaiveVEBEnrollmentPayload;
export const generateMaxCareDentalWaiveEnrollmentPayload = generateWaiveVEBEnrollmentPayload;
export const generateBeazelyGroupLifeIndemnityWaiveEnrollmentPayload = generateWaiveVEBEnrollmentPayload;
export const generateShortTermDisabilityWaiveEnrollmentPayload = generateWaiveVEBEnrollmentPayload;
